import * as microsoftTeams from '@microsoft/teams-js';

/**
 * Thrown by `api()` on a non-2xx response. `message` keeps the exact
 * `` `API GET /path → 500: {...}` `` shape existing tests and callers already
 * rely on — this only *adds* a structured `status`, so a caught error's HTTP
 * status can be switched on directly instead of parsed back out of the
 * message string (#154). Screens map `status` to the wireframe's E3/S3b copy
 * via `components/ApiErrorMessage.tsx` rather than rendering `message`.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Thin fetch wrapper: attaches the Teams SSO token as a Bearer token.
 * Same-origin in production (SPA served by the API container); the Vite dev
 * proxy handles /api in development.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await microsoftTeams.authentication.getAuthToken();

  // `init.headers` is HeadersInit — it may be a Headers instance or an array of
  // pairs, and object-spreading either of those silently drops the caller's
  // headers (or turns them into numeric indices). Build a Headers and let it
  // normalise, so callers can pass any of the three accepted shapes.
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  // Let the browser set Content-Type (plus the multipart boundary) for FormData.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `API ${init.method ?? 'GET'} ${path} → ${res.status}: ${body}`);
  }
  // A 204 (e.g. DELETE /api/requests/:id, #33) has no body — `res.json()`
  // throws on the empty string rather than returning anything sensible.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
